<template>
  <v-card flat outlined color="tiffany-1" class="rounded-lg">
    <v-card-title class="text-subtitle-1 font-weight-bold tiffany-8--text">
      <v-icon left color="tiffany-8">{{ isNew ? 'mdi-calendar-plus' : 'mdi-calendar-edit' }}</v-icon>
      {{ isNew ? '新增休診規則' : '編輯休診規則' }}
    </v-card-title>
    <v-divider />

    <v-card-text class="pa-4 pa-md-6">
      <div class="mb-6">
        <div class="text-caption font-weight-bold grey-5--text mb-2">規則類型</div>
        <v-item-group :value="value.editor_type" mandatory @change="changeEditorType">
          <v-row dense>
            <v-col v-for="option in editorTypeOptions" :key="option.value" cols="6" sm="3">
              <v-item v-slot="{ active, toggle }" :value="option.value">
                <v-btn
                  block
                  depressed
                  :color="active ? 'tiffany-8' : 'white'"
                  :dark="active"
                  :class="{ 'grey-5--text': !active }"
                  @click="toggle"
                >
                  {{ option.text }}
                </v-btn>
              </v-item>
            </v-col>
          </v-row>
        </v-item-group>
        <div v-if="value.editor_type === 'day'" class="d-flex align-center text-caption grey-7--text mt-2">
          <v-icon small class="mr-1">mdi-information-outline</v-icon>
          每天在相同時段休診
        </div>
      </div>

      <div v-if="value.editor_type === 'week'" class="mb-6">
        <div class="text-caption font-weight-bold grey-5--text mb-2">每週休診日</div>
        <v-item-group :value="value.weekdays" multiple @change="updateArrayField('weekdays', $event)">
          <v-row dense>
            <v-col v-for="option in weekdayOptions" :key="option.value" cols="3" sm="auto">
              <v-item v-slot="{ active, toggle }" :value="option.value">
                <v-btn
                  block
                  depressed
                  :color="active ? 'tiffany-8' : 'white'"
                  :dark="active"
                  :class="{ 'grey-5--text': !active }"
                  @click="toggle"
                >
                  {{ option.text.replace('週', '') }}
                </v-btn>
              </v-item>
            </v-col>
          </v-row>
        </v-item-group>
      </div>

      <template v-if="value.editor_type === 'cycle'">
        <v-row class="mb-2">
          <v-col cols="12" md="4">
            <div class="text-caption font-weight-bold grey-5--text mb-2">循環週次</div>
            <v-select
              :value="value.rule_type"
              :items="cycleOptions"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              background-color="white"
              @change="updateField('rule_type', $event)"
            />
          </v-col>
          <v-col cols="12" md="8">
            <div class="text-caption font-weight-bold grey-5--text mb-2">每週休診日</div>
            <v-item-group :value="value.weekdays" multiple @change="updateArrayField('weekdays', $event)">
              <v-row dense>
                <v-col v-for="option in weekdayOptions" :key="option.value" cols="3" sm="auto">
                  <v-item v-slot="{ active, toggle }" :value="option.value">
                    <v-btn
                      block
                      depressed
                      :color="active ? 'tiffany-8' : 'white'"
                      :dark="active"
                      :class="{ 'grey-5--text': !active }"
                      @click="toggle"
                    >
                      {{ option.text.replace('週', '') }}
                    </v-btn>
                  </v-item>
                </v-col>
              </v-row>
            </v-item-group>
          </v-col>
        </v-row>

        <div v-if="value.rule_type === 'selected_weeks'" class="mb-6">
          <div class="text-caption font-weight-bold grey-5--text mb-2">指定每月週次</div>
          <v-item-group :value="value.month_weeks" multiple @change="updateArrayField('month_weeks', $event)">
            <v-row dense>
              <v-col v-for="week in monthWeekOptions" :key="week" cols="4" sm="auto">
                <v-item v-slot="{ active, toggle }" :value="week">
                  <v-btn
                    block
                    depressed
                    :color="active ? 'tiffany-8' : 'white'"
                    :dark="active"
                    :class="{ 'grey-5--text': !active }"
                    @click="toggle"
                  >
                    第 {{ week }} 週
                  </v-btn>
                </v-item>
              </v-col>
            </v-row>
          </v-item-group>
        </div>
      </template>

      <template v-if="value.editor_type === 'date'">
        <v-row>
          <v-col cols="12" md="5">
            <div class="text-caption font-weight-bold grey-5--text mb-2">日期</div>
            <v-text-field
              :value="value.specific_date"
              type="date"
              dense
              outlined
              hide-details
              background-color="white"
              @input="updateField('specific_date', $event)"
            />
          </v-col>
          <v-col cols="12" md="7">
            <div class="text-caption font-weight-bold grey-5--text mb-2">當日狀態</div>
            <v-btn-toggle
              :value="value.action_type"
              mandatory
              dense
              color="tiffany-8"
              @change="updateField('action_type', $event)"
            >
              <v-btn value="closed">休診</v-btn>
              <v-btn value="open">照常營業</v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <div class="mb-6">
          <div class="text-caption font-weight-bold grey-5--text mb-2">備註（選填）</div>
          <v-textarea
            :value="value.note"
            maxlength="255"
            counter="255"
            rows="2"
            dense
            outlined
            no-resize
            hide-details="auto"
            background-color="white"
            placeholder="例：中秋節、員工教育訓練"
            @input="updateField('note', $event)"
          />
          <div class="text-caption grey-7--text mt-1">備註僅供 CRM 管理畫面辨識，不會顯示給患者。</div>
        </div>
      </template>

      <div>
        <div class="text-caption font-weight-bold grey-5--text mb-1">時間</div>
        <v-checkbox
          :input-value="value.is_all_day"
          label="全天"
          color="tiffany-8"
          hide-details
          class="mt-0 mb-3"
          @change="updateField('is_all_day', $event)"
        />

        <template v-if="!value.is_all_day">
          <v-row v-for="(period, index) in value.periods" :key="index" dense align="end" class="mb-1">
            <v-col cols="12" sm="5">
              <offwork-time-picker
                :value="period.start"
                label="開始時間"
                @input="updatePeriod(index, 'start', $event)"
              />
            </v-col>
            <v-col cols="1" class="d-none d-sm-flex justify-center pb-4 grey-7--text">到</v-col>
            <v-col cols="10" sm="5">
              <offwork-time-picker
                :value="period.end"
                label="結束時間"
                allow-end-of-day
                @input="updatePeriod(index, 'end', $event)"
              />
            </v-col>
            <v-col cols="2" sm="1" class="d-flex justify-center pb-2">
              <v-btn
                icon
                small
                color="grey-7"
                :disabled="value.periods.length === 1"
                aria-label="刪除時間區段"
                @click="removePeriod(index)"
              >
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-btn text small color="tiffany-8" class="px-0" @click="addPeriod">
            <v-icon small left>mdi-plus</v-icon>
            新增時間區段
          </v-btn>
          <div class="text-caption grey-7--text mt-1">結束時間早於開始時間時，系統會視為隔日結束。</div>
        </template>
      </div>

      <v-alert v-if="showError && validationError" dense outlined type="error" class="mt-5 mb-0">
        {{ validationError }}
      </v-alert>
    </v-card-text>

    <v-divider />
    <v-card-actions class="pa-4">
      <v-spacer />
      <v-btn text color="grey-7" @click="$emit('cancel')">取消</v-btn>
      <v-btn depressed color="tiffany-8" dark class="px-5" @click="finish">
        <v-icon small left>mdi-check</v-icon>
        {{ isNew ? '加入清單' : '完成編輯' }}
      </v-btn>
    </v-card-actions>
    <v-alert dense text type="info" class="mx-4 mb-4 text-body-2">
      {{ isNew ? '加入清單' : '完成編輯' }}後，還要按「確認儲存」才會保存到系統。
    </v-alert>
  </v-card>
</template>

<script>
import OffworkTimePicker from './OffworkTimePicker'
import {
  MONTH_WEEK_OPTIONS,
  WEEKDAY_OPTIONS,
  cloneOffworkValue,
  validateOffworkRule
} from '@/utils/offworkAutoreply'

export default {
  name: 'OffworkRuleEditor',
  components: {
    OffworkTimePicker
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    isNew: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showError: false,
      editorTypeOptions: [
        { value: 'day', text: '每天固定' },
        { value: 'week', text: '每週固定' },
        { value: 'cycle', text: '週期性' },
        { value: 'date', text: '特定日' }
      ],
      cycleOptions: [
        { value: 'odd_weeks', text: '每月單數週' },
        { value: 'even_weeks', text: '每月雙數週' },
        { value: 'selected_weeks', text: '每月指定週次' }
      ],
      weekdayOptions: WEEKDAY_OPTIONS,
      monthWeekOptions: MONTH_WEEK_OPTIONS
    }
  },
  computed: {
    validationError() {
      return validateOffworkRule(this.value)
    }
  },
  watch: {
    value: {
      deep: true,
      handler() {
        if (this.showError && !this.validationError) this.showError = false
      }
    }
  },
  methods: {
    emitValue(nextValue) {
      this.$emit('input', nextValue)
    },
    updateField(field, fieldValue) {
      const nextValue = cloneOffworkValue(this.value)
      nextValue[field] = fieldValue
      this.emitValue(nextValue)
    },
    changeEditorType(editorType) {
      const nextValue = cloneOffworkValue(this.value)
      nextValue.editor_type = editorType
      nextValue.action_type = 'closed'

      if (editorType === 'cycle' && !['odd_weeks', 'even_weeks', 'selected_weeks'].includes(nextValue.rule_type)) {
        nextValue.rule_type = 'odd_weeks'
      } else if (editorType === 'date') {
        nextValue.rule_type = 'specific_date'
      } else if (['day', 'week'].includes(editorType)) {
        nextValue.rule_type = 'weekly'
      }

      this.emitValue(nextValue)
    },
    updateArrayField(field, fieldValues) {
      const nextValue = cloneOffworkValue(this.value)
      nextValue[field] = [...(fieldValues || [])].sort((left, right) => left - right)
      this.emitValue(nextValue)
    },
    updatePeriod(index, field, fieldValue) {
      const nextValue = cloneOffworkValue(this.value)
      nextValue.periods[index][field] = fieldValue
      this.emitValue(nextValue)
    },
    addPeriod() {
      const nextValue = cloneOffworkValue(this.value)
      nextValue.periods.push({ start: '18:05', end: '20:00' })
      this.emitValue(nextValue)
    },
    removePeriod(index) {
      if (this.value.periods.length === 1) return
      const nextValue = cloneOffworkValue(this.value)
      nextValue.periods.splice(index, 1)
      this.emitValue(nextValue)
    },
    finish() {
      this.showError = true
      if (this.validationError) return
      this.$emit('done')
    }
  }
}
</script>
